/* NotificationsBell — bell icon w/ unread badge + popover drawer.
 *
 * Polls /api/admin/notifications/unread-count every 60s. Opening the panel
 * triggers a full list fetch. Clicking an item marks it read and navigates
 * to its link (if any). "Mark all read" clears the badge.
 *
 * Drop into any admin header — it's self-contained.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  Bell, BellRing, X, CheckCheck, Trash2, Loader2,
  AlertTriangle, Trophy, BookOpen, Sparkles, Clock,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ICONS = {
  quiz_failed: AlertTriangle,
  track_assigned: BookOpen,
  track_completed: Trophy,
  training_reminder: Clock,
  info: Sparkles,
};
const TYPE_COLORS = {
  quiz_failed: 'text-rose-600 bg-rose-50',
  track_assigned: 'text-sky-700 bg-sky-50',
  track_completed: 'text-emerald-700 bg-emerald-50',
  training_reminder: 'text-amber-700 bg-amber-50',
  info: 'text-[#4B5563] bg-[#FAFBFC]',
};

const relTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const s = (Date.now() - d.getTime()) / 1000;
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return d.toLocaleDateString();
};

const NotificationsBell = () => {
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const popRef = useRef(null);

  const fetchCount = useCallback(async () => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/admin/notifications/unread-count`,
        { withCredentials: true });
      setUnread(r.data?.unread_count || 0);
    } catch (_) { /* silent — bell is best-effort */ }
  }, []);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${BACKEND_URL}/api/admin/notifications?limit=50`,
        { withCredentials: true });
      setRows(r.data?.rows || []);
      setUnread(r.data?.unread_count || 0);
    } catch (_) { /* silent */ }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchCount();
    const id = setInterval(fetchCount, 60_000);
    return () => clearInterval(id);
  }, [fetchCount]);

  // Reflect unread count in the browser tab title — "(3) NHF Admin".
  // Re-applies whenever the count changes; restores on unmount.
  useEffect(() => {
    const original = document.title.replace(/^\(\d+\)\s*/, '');
    document.title = unread > 0 ? `(${unread > 99 ? '99+' : unread}) ${original}` : original;
    return () => {
      document.title = document.title.replace(/^\(\d+\)\s*/, '');
    };
  }, [unread]);

  useEffect(() => {
    if (open) fetchAll();
  }, [open, fetchAll]);

  // Close on outside click.
  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => {
      if (popRef.current && !popRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const markRead = async (id) => {
    setRows((rs) => rs.map((r) => (r.id === id ? { ...r, read: true } : r)));
    setUnread((n) => Math.max(0, n - 1));
    try {
      await axios.put(`${BACKEND_URL}/api/admin/notifications/${id}/read`, {},
        { withCredentials: true });
    } catch (_) { /* silent */ }
  };

  const markAll = async () => {
    setRows((rs) => rs.map((r) => ({ ...r, read: true })));
    setUnread(0);
    try {
      await axios.put(`${BACKEND_URL}/api/admin/notifications/mark-all-read`, {},
        { withCredentials: true });
    } catch (_) { /* silent */ }
  };

  const remove = async (id) => {
    setRows((rs) => rs.filter((r) => r.id !== id));
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/notifications/${id}`,
        { withCredentials: true });
    } catch (_) { /* silent */ }
    fetchCount();
  };

  const BellIcon = unread > 0 ? BellRing : Bell;

  return (
    <div className="relative" ref={popRef} data-testid="notifications-bell-root">
      <button
        type="button"
        onClick={() => setOpen((x) => !x)}
        className="relative p-2 rounded hover:bg-[#FAFBFC] transition-colors text-[#4B5563]"
        title={unread > 0 ? `${unread} unread notification${unread === 1 ? '' : 's'}` : 'Notifications'}
        data-testid="notifications-bell">
        <BellIcon className={`w-5 h-5 ${unread > 0 ? 'text-[#BA1826]' : ''}`} />
        {unread > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-[#BA1826] text-white text-[9px] font-bold flex items-center justify-center"
            data-testid="notifications-badge">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-[360px] max-h-[500px] bg-white border border-[#E5E7EB] rounded-lg shadow-xl z-50 flex flex-col overflow-hidden"
          data-testid="notifications-panel">
          <div className="px-3 py-2.5 border-b border-[#E5E7EB] flex items-center justify-between bg-[#FAFBFC]">
            <h3 className="text-xs font-bold uppercase tracking-wider text-[#0A0A0A] inline-flex items-center gap-1.5">
              <Bell className="w-3.5 h-3.5 text-[#BA1826]" /> Notifications
              {unread > 0 && (
                <span className="text-[10px] text-[#BA1826] font-mono normal-case">
                  ({unread} unread)
                </span>
              )}
            </h3>
            <div className="flex items-center gap-1">
              {unread > 0 && (
                <button onClick={markAll}
                  className="text-[10px] text-[#4B5563] hover:text-[#BA1826] inline-flex items-center gap-1"
                  data-testid="notifications-mark-all-read">
                  <CheckCheck className="w-3 h-3" /> Mark all read
                </button>
              )}
              <button onClick={() => setOpen(false)}
                className="text-[#9CA3AF] hover:text-[#BA1826] p-0.5"
                aria-label="Close">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-5 h-5 animate-spin text-[#BA1826]" />
              </div>
            ) : rows.length === 0 ? (
              <p className="text-center py-8 text-xs text-[#9CA3AF] italic">
                You&apos;re all caught up. Nothing to see here.
              </p>
            ) : (
              <ul className="divide-y divide-[#F1F5F9]">
                {rows.map((r) => {
                  const Icon = ICONS[r.type] || Sparkles;
                  const swatch = TYPE_COLORS[r.type] || TYPE_COLORS.info;
                  const Body = (
                    <div className="flex items-start gap-2.5 p-3">
                      <span className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${swatch}`}>
                        <Icon className="w-3.5 h-3.5" />
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className={`text-xs ${r.read ? 'text-[#4B5563]' : 'font-semibold text-[#0A0A0A]'}`}>
                          {r.title}
                        </p>
                        {r.message && (
                          <p className="text-[11px] text-[#4B5563] mt-0.5 leading-snug">
                            {r.message}
                          </p>
                        )}
                        <p className="text-[10px] text-[#9CA3AF] mt-1">{relTime(r.created_at)}</p>
                      </div>
                      {!r.read && (
                        <span className="w-1.5 h-1.5 rounded-full bg-[#BA1826] mt-2 shrink-0"
                          aria-label="Unread" />
                      )}
                      <button
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); remove(r.id); }}
                        className="text-[#9CA3AF] hover:text-[#BA1826] p-0.5 mt-0.5"
                        data-testid={`notif-delete-${r.id}`}
                        title="Dismiss">
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  );

                  const handleClick = () => {
                    if (!r.read) markRead(r.id);
                    if (r.link) setOpen(false);
                  };

                  if (r.link) {
                    return (
                      <li key={r.id} data-testid={`notif-row-${r.id}`}>
                        <Link to={r.link} onClick={handleClick}
                          className={`block hover:bg-[#FAFBFC] transition-colors ${r.read ? '' : 'bg-[#FFF8F8]'}`}>
                          {Body}
                        </Link>
                      </li>
                    );
                  }
                  return (
                    <li key={r.id} data-testid={`notif-row-${r.id}`}>
                      <button onClick={handleClick}
                        className={`w-full text-left hover:bg-[#FAFBFC] transition-colors ${r.read ? '' : 'bg-[#FFF8F8]'}`}>
                        {Body}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationsBell;
